import { useEffect, useRef, useState } from "react";
import {
  AlertCircle, ArrowRight, Eye, KeyRound, Layers, Mail, Palette, Send, Shield, User,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { Logo, LogoMark } from "../components/branding";

const cleanError = (e) =>
  (e && e.detail) || (e && e.message ? e.message.replace(/^\d+\s*/, "") : "") || "Something went wrong";

const PANEL_FEATURES = [
  { icon: Eye, title: "Be seen", description: "Show up consistently on every feed your audience already scrolls." },
  { icon: Layers, title: "One source", description: "Product updates, company values, and brand voice, all in one place." },
  { icon: Palette, title: "On brand", description: "Your tone and style, kept consistent across every channel." },
  { icon: Send, title: "Native everywhere", description: "Each post shaped for its platform, from X to Reddit." },
];

export default function AuthPage({ onBack }) {
  const { requestCode, verifyCode } = useAuth();

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("request"); // 'request' | 'verify'
  const [error, setError] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const codeInputRef = useRef(null);
  const verifyingRef = useRef(false);

  useEffect(() => {
    document.title = "Sign in to Remifo";
  }, []);

  // Resend cooldown ticker
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // Focus the code field on the verify step
  useEffect(() => {
    if (step === "verify") setTimeout(() => codeInputRef.current && codeInputRef.current.focus(), 100);
  }, [step]);

  // Auto-submit once 6 digits are entered
  useEffect(() => {
    if (step === "verify" && code.length === 6 && !verifyingRef.current) handleVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleRequest = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      await requestCode(email.trim(), isSignup && fullName.trim() ? fullName.trim() : null);
      setStep("verify");
      setResendIn(30);
    } catch (err) {
      const msg = cleanError(err);
      if (msg.toLowerCase().includes("full name is required") || (err.status === 400 && /name/i.test(err.detail || ""))) {
        setIsSignup(true);
        setError("No account found with this email. Add your name to create one.");
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    if (code.length !== 6 || verifyingRef.current) return;
    verifyingRef.current = true;
    setIsLoading(true);
    setError("");
    try {
      await verifyCode(email.trim(), code);
      // On success the auth context flips the app to the signed in view.
    } catch (err) {
      setError(cleanError(err));
      setCode("");
      setTimeout(() => codeInputRef.current && codeInputRef.current.focus(), 50);
    } finally {
      setIsLoading(false);
      verifyingRef.current = false;
    }
  };

  const handleResend = async () => {
    if (resendIn > 0) return;
    setIsLoading(true);
    setError("");
    try {
      await requestCode(email.trim(), isSignup && fullName.trim() ? fullName.trim() : null);
      setCode("");
      setResendIn(30);
    } catch (err) {
      setError(cleanError(err));
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignup(!isSignup);
    setError("");
    setFullName("");
  };
  const backToRequest = () => {
    setStep("request");
    setCode("");
    setError("");
  };

  const ErrorBox = ({ children }) =>
    children ? (
      <div className="bg-foreground/5 border border-foreground/15 rounded-xl p-3 flex items-start gap-2.5 animate-fade-in">
        <AlertCircle size={18} className="text-foreground shrink-0 mt-0.5" />
        <p className="text-sm text-foreground/80 font-light">{children}</p>
      </div>
    ) : null;

  // ---- Verify (code entry) ----
  if (step === "verify") {
    return (
      <div className="min-h-[100dvh] bg-background flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md animate-fade-in">
          <div className="bg-foreground/5 rounded-2xl border border-border overflow-hidden">
            <div className="bg-accent p-6 sm:p-8 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-accent-foreground/10 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <KeyRound size={28} className="text-accent-foreground" />
              </div>
              <h1 className="text-xl sm:text-2xl font-light text-accent-foreground mb-1.5">Enter your code</h1>
              <p className="text-sm sm:text-base text-accent-foreground/60 font-light">
                We sent a 6 digit code to your email
              </p>
            </div>

            <div className="p-5 sm:p-8">
              <div className="text-center mb-5 sm:mb-6">
                <p className="text-sm text-foreground/60 font-light mb-2">Code sent to</p>
                <p className="text-sm font-medium text-foreground bg-foreground/5 border border-border px-4 py-2.5 rounded-xl break-all">
                  {email}
                </p>
              </div>

              <form onSubmit={handleVerify}>
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  disabled={isLoading}
                  className="w-full text-center text-2xl sm:text-3xl font-light tracking-[0.5em] text-foreground bg-foreground/5 border border-border rounded-xl py-4 sm:py-5 mb-4 placeholder:text-foreground/20 focus:outline-none focus:border-foreground/30 transition-all"
                />

                <div className="mb-4">
                  <ErrorBox>{error}</ErrorBox>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="w-full flex items-center justify-center gap-2 bg-accent text-accent-foreground py-3.5 rounded-xl hover:opacity-90 transition text-sm sm:text-base font-medium disabled:opacity-50 active:scale-[0.98] mb-3"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                      Verifying
                    </>
                  ) : (
                    <>Verify and continue<ArrowRight size={18} /></>
                  )}
                </button>
              </form>

              <button
                onClick={backToRequest}
                className="w-full bg-foreground/5 text-foreground py-3 rounded-xl hover:bg-foreground/10 transition font-light mb-4 text-sm active:scale-[0.98]"
              >
                Use a different email
              </button>

              <p className="text-center text-xs sm:text-sm text-foreground/40 font-light">
                Did not get it?{" "}
                {resendIn > 0 ? (
                  <span>Resend in {resendIn}s</span>
                ) : (
                  <button onClick={handleResend} disabled={isLoading} className="text-foreground hover:underline font-light disabled:opacity-50">
                    {isLoading ? "Sending" : "Resend code"}
                  </button>
                )}
              </p>
            </div>
          </div>

          <div className="text-center mt-6">
            <button onClick={onBack} className="text-sm text-foreground/40 hover:text-foreground transition-colors font-light">
              Back to home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Request (email) ----
  return (
    <div className="min-h-[100dvh] bg-background">
      <div className="flex min-h-[100dvh]">
        {/* Left: themed form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-6">
          <div className="w-full max-w-md animate-fade-in">
            <div className="text-center mb-6 sm:mb-8">
              <button onClick={onBack} className="inline-flex items-center hover:opacity-70 transition-opacity">
                <Logo size={34} />
              </button>
            </div>

            <div className="bg-foreground/5 rounded-2xl border border-border p-5 sm:p-8">
              {/* Segmented Sign In / Sign Up */}
              <div className="relative bg-foreground/5 rounded-xl p-1 mb-6 sm:mb-8 border border-border">
                <div
                  className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-accent rounded-lg transition-transform duration-300 ease-ios"
                  style={{ transform: !isSignup ? "translateX(0)" : "translateX(100%)" }}
                />
                <div className="relative grid grid-cols-2">
                  <button
                    type="button"
                    onClick={() => isSignup && switchMode()}
                    className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-300 ${!isSignup ? "text-accent-foreground" : "text-foreground/55"}`}
                  >
                    Sign in
                  </button>
                  <button
                    type="button"
                    onClick={() => !isSignup && switchMode()}
                    className={`py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-300 ${isSignup ? "text-accent-foreground" : "text-foreground/55"}`}
                  >
                    Sign up
                  </button>
                </div>
              </div>

              <div className="text-center mb-6 sm:mb-8">
                <h1 className="text-xl sm:text-2xl font-light text-foreground mb-1.5">
                  {isSignup ? "Create your account" : "Welcome back"}
                </h1>
                <p className="text-sm sm:text-base text-foreground/60 font-light">
                  {isSignup ? "Start building your content calendar" : "Enter your email to continue"}
                </p>
              </div>

              <form onSubmit={handleRequest} className="space-y-4 sm:space-y-5">
                <ErrorBox>{error}</ErrorBox>

                <div className={`space-y-1.5 transition-all duration-300 ease-out ${isSignup ? "opacity-100 max-h-24" : "opacity-0 max-h-0 overflow-hidden"}`}>
                  <label htmlFor="fullName" className="block text-xs sm:text-sm font-light text-foreground/70">Name</label>
                  <div className="relative">
                    <input
                      id="fullName"
                      type="text"
                      placeholder="Your name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 sm:py-3.5 bg-foreground/5 border border-border rounded-xl text-sm sm:text-base text-foreground font-light placeholder:text-foreground/30 focus:outline-none focus:border-foreground/30 transition-all"
                      required={isSignup}
                      disabled={isLoading || !isSignup}
                      tabIndex={isSignup ? 0 : -1}
                    />
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="email" className="block text-xs sm:text-sm font-light text-foreground/70">Email</label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 sm:py-3.5 bg-foreground/5 border border-border rounded-xl text-sm sm:text-base text-foreground font-light placeholder:text-foreground/30 focus:outline-none focus:border-foreground/30 transition-all"
                      required
                      disabled={isLoading}
                      autoComplete="email"
                    />
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/40" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email.trim() || (isSignup && !fullName.trim())}
                  className="w-full flex items-center justify-center gap-2 bg-accent text-accent-foreground py-3 sm:py-3.5 rounded-xl hover:opacity-90 transition text-sm sm:text-base font-medium disabled:opacity-50 active:scale-[0.98]"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                      Sending code
                    </>
                  ) : (
                    <>{isSignup ? "Create account" : "Send code"}<ArrowRight size={18} /></>
                  )}
                </button>

                <div className="p-3 sm:p-4 bg-foreground/5 rounded-xl border border-border">
                  <div className="flex items-center gap-3 text-foreground/60">
                    <Shield size={16} className="text-foreground/40 shrink-0" />
                    <p className="text-xs sm:text-sm font-light">
                      <span className="text-foreground">Passwordless.</span> We email you a 6 digit code.
                    </p>
                  </div>
                </div>
              </form>
            </div>

            <div className="text-center mt-5 sm:mt-6">
              <button onClick={onBack} className="text-foreground/40 hover:text-foreground text-sm font-light transition-colors">
                Back to home
              </button>
            </div>
          </div>
        </div>

        {/* Right: brand panel, kept dark on purpose */}
        <div className="hidden lg:flex lg:w-1/2 bg-black p-12 items-center border-l border-white/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent" />
          <div className="text-white max-w-lg mx-auto relative z-10">
            <div className="mb-12">
              <h2 className="text-4xl font-light mb-4 leading-tight tracking-tight">
                Your work, seen on <span className="text-white/50">every feed.</span>
              </h2>
              <p className="text-lg text-white/50 font-light">
                Remifo turns your updates, values, and voice into posts made for each platform.
              </p>
            </div>

            <div className="space-y-6">
              {PANEL_FEATURES.map((item) => (
                <div key={item.title} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0 group-hover:bg-white/15 transition-colors">
                    <item.icon size={18} className="text-white/70" strokeWidth={1.6} />
                  </div>
                  <div>
                    <h3 className="text-lg font-light mb-1 text-white">{item.title}</h3>
                    <p className="text-white/50 font-light text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 p-6 bg-white/5 rounded-2xl border border-white/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white text-black flex items-center justify-center">
                  <LogoMark size={22} />
                </div>
                <div>
                  <p className="font-medium text-white leading-tight">Your brand</p>
                  <p className="text-sm text-white/45 font-light">@yourbrand</p>
                </div>
              </div>
              <p className="text-sm text-white/70 font-light leading-relaxed">
                The changelog shipped Tuesday. Here is the one change that quietly saves your team an hour a day.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {["#product", "#changelog", "#build"].map((t) => (
                  <span key={t} className="text-xs text-white/40 font-light">{t}</span>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-xs text-white/30 font-light">Posted by remifo</span>
                <span className="text-xs text-white/30 font-light">X, LinkedIn, Reddit</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}